import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { InviteMemberForm } from "@/components/auth/InviteMemberForm";
import { useNavigate, useLocation } from "react-router-dom";
import { Integrations } from "@/components/settings/Integrations";

// Team member type
interface TeamMember {
  user_id: string;
  name: string;
  email: string;
  role: "admin" | "editor" | "viewer";
  created_at: string;
}

interface Organization {
  organization_id: string;
  name: string;
}

export function Settings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userRole, setUserRole] = useState<string>("viewer");
  const navigate = useNavigate();
  const location = useLocation();
  
  // Set the default tab based on URL parameter
  const getDefaultTab = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['profile', 'team', 'integrations', 'billing'].includes(tab)) {
      return tab;
    }
    return 'profile';
  };
  
  // Fetch organization and team members
  useEffect(() => {
    const fetchOrganizationAndMembers = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Fetch the current user's data to get organization ID and role
        const { data: currentUser, error: userError } = await supabase
          .from('users')
          .select('organization_id, role')
          .eq('user_id', user.id)
          .single();
          
        if (userError || !currentUser?.organization_id) {
          toast.error('Failed to load user data');
          return;
        }
        
        setUserRole(currentUser.role);
        
        // Fetch organization details
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('organization_id, name')
          .eq('organization_id', currentUser.organization_id)
          .single();
          
        if (orgError) {
          toast.error('Failed to load organization data');
          return;
        }
        
        setOrganization(orgData);
        
        // Fetch team members
        const { data: members, error: membersError } = await supabase
          .from('users')
          .select('user_id, name, email, role, created_at')
          .eq('organization_id', currentUser.organization_id)
          .order('created_at', { ascending: true });
          
        if (membersError) {
          toast.error('Failed to load team members');
          return;
        }
        
        setTeamMembers(members || []);
      } catch (error) {
        toast.error('An error occurred while loading data');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrganizationAndMembers();
  }, [user]);
  
  const handleRoleChange = async (memberId: string, newRole: "admin" | "editor" | "viewer") => {
    // Only admins can change roles
    if (userRole !== 'admin') {
      toast.error('Only admins can change user roles');
      return;
    }
    
    try {
      // Prevent demoting the last admin
      if (newRole !== 'admin') {
        const currentAdmins = teamMembers.filter(member => member.role === 'admin');
        if (currentAdmins.length === 1 && currentAdmins[0].user_id === memberId) {
          toast.error('Cannot change the last admin. Promote another user to admin first.');
          return;
        }
      }
      
      const { error } = await supabase
        .from('users')
        .update({ role: newRole })
        .eq('user_id', memberId);
        
      if (error) throw error;
      
      setTeamMembers(members =>
        members.map(member => 
          member.user_id === memberId ? { ...member, role: newRole } : member
        )
      );
      
      toast.success('User role updated successfully');
    } catch (error) {
      toast.error('Failed to update user role');
      console.error(error);
    }
  };
  
  const handleRemoveMember = async (memberId: string) => {
    // Only admins can remove members
    if (userRole !== 'admin') {
      toast.error('Only admins can remove team members');
      return;
    }
    
    // Prevent removing yourself
    if (memberId === user?.id) {
      toast.error('You cannot remove yourself from the organization');
      return;
    }
    
    try {
      // In a real application, you might want to implement a "soft delete" 
      // or move the user to an "unassigned" state rather than deleting them
      const { error } = await supabase
        .from('users')
        .update({ organization_id: null })
        .eq('user_id', memberId);
        
      if (error) throw error;
      
      setTeamMembers(members => members.filter(member => member.user_id !== memberId));
      toast.success('Team member removed successfully');
    } catch (error) {
      toast.error('Failed to remove team member');
      console.error(error);
    }
  };
  
  // Fetch pending invitations
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  
  const fetchPendingInvitations = async () => {
    if (!organization) return;
    
    try {
      setLoadingInvitations(true);
      const { data, error } = await supabase
        .from('invitations')
        .select('invitation_id, email, role, created_at, expires_at')
        .eq('organization_id', organization.organization_id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setPendingInvitations(data || []);
    } catch (error) {
      console.error('Failed to load pending invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  };
  
  useEffect(() => {
    if (organization) {
      fetchPendingInvitations();
    }
  }, [organization]);
  
  const handleCancelInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('invitations')
        .update({ status: 'canceled' })
        .eq('invitation_id', invitationId);
        
      if (error) throw error;
      
      setPendingInvitations(invitations => 
        invitations.filter(invitation => invitation.invitation_id !== invitationId)
      );
      
      toast.success('Invitation canceled');
    } catch (error) {
      toast.error('Failed to cancel invitation');
      console.error(error);
    }
  };
  
  const handleInviteSuccess = () => {
    fetchPendingInvitations();
  };
  
  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">Settings</h1>
        <p className="text-sm sm:text-base md:text-lg text-muted-foreground mt-1 sm:mt-1.5">
          Manage your account settings and preferences
        </p>
      </div>
      
      <Tabs defaultValue={getDefaultTab()} className="w-full">
        <TabsList className="grid w-full grid-cols-4 h-auto md:h-10 md:w-auto md:inline-flex">
          <TabsTrigger value="profile" className="text-xs sm:text-sm py-2 px-1 sm:px-3">Profile</TabsTrigger>
          <TabsTrigger value="team" className="text-xs sm:text-sm py-2 px-1 sm:px-3">Team</TabsTrigger>
          <TabsTrigger value="integrations" className="text-xs sm:text-sm py-2 px-1 sm:px-3">Integrations</TabsTrigger>
          <TabsTrigger value="billing" className="text-xs sm:text-sm py-2 px-1 sm:px-3">Billing</TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile" className="mt-4 sm:mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your personal information and how others see you on the platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input id="name" defaultValue={user?.user_metadata?.name || user?.email?.split('@')[0]} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" defaultValue={user?.email} disabled />
                  </div>
                </div>
                <Separator />
                <div className="flex justify-end">
                  <Button>Save Changes</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="team" className="mt-4 sm:mt-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Organization info */}
              {organization && (
                <Card>
                  <CardHeader>
                    <CardTitle>Organization</CardTitle>
                    <CardDescription>
                      Your organization settings and information
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label>Organization Name</Label>
                        <div className="text-sm font-medium">{organization.name}</div>
                      </div>
                      <div className="space-y-1">
                        <Label>Your Role</Label>
                        <div className="text-sm font-medium capitalize">{userRole}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            
              {/* Invite team members */}
              <Card>
                <CardHeader>
                  <CardTitle>Invite Team Members</CardTitle>
                  <CardDescription>
                    Invite colleagues to join your organization
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {(userRole === 'admin' || userRole === 'editor') && organization ? (
                    <InviteMemberForm 
                      organizationId={organization.organization_id} 
                      userRole={userRole}
                      onInviteSuccess={handleInviteSuccess}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      You need editor or admin privileges to invite team members.
                    </p>
                  )}
                  
                  {/* Pending invitations */}
                  {(userRole === 'admin' || userRole === 'editor') && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <h3 className="text-sm font-medium">Pending Invitations</h3>
                        {loadingInvitations ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : pendingInvitations.length > 0 ? (
                          <div className="space-y-2">
                            {pendingInvitations.map((invitation) => (
                              <div key={invitation.invitation_id} className="flex items-center justify-between p-3 bg-accent/40 rounded-lg">
                                <div>
                                  <p className="text-sm font-medium">{invitation.email}</p>
                                  <p className="text-xs text-muted-foreground">
                                    Invited as <span className="capitalize">{invitation.role}</span> on{' '}
                                    {new Date(invitation.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-destructive hover:text-destructive/80"
                                  onClick={() => handleCancelInvitation(invitation.invitation_id)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground py-2">No pending invitations</p>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            
              {/* Team members list */}
              <Card>
                <CardHeader>
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>
                    Manage your team and their access levels
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {teamMembers.map((member) => (
                      <div key={member.user_id} className="flex items-center justify-between p-3 bg-accent/40 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback>{(member.name || member.email.charAt(0)).charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{member.name || member.email.split('@')[0]}</p>
                            <p className="text-xs text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {userRole === 'admin' ? (
                            <>
                              <Select 
                                defaultValue={member.role}
                                onValueChange={(value) => handleRoleChange(member.user_id, value as "admin" | "editor" | "viewer")}
                                disabled={member.user_id === user?.id} // Can't change your own role
                              >
                                <SelectTrigger className="h-8 text-xs w-24">
                                  <SelectValue placeholder="Role" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                  <SelectItem value="editor">Editor</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                              {member.user_id !== user?.id && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                  onClick={() => handleRemoveMember(member.user_id)}
                                >
                                  <span className="sr-only">Remove member</span>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                                </Button>
                              )}
                            </>
                          ) : (
                            <span className="text-xs capitalize px-2 py-1 rounded-full bg-muted">{member.role}</span>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {teamMembers.length === 0 && (
                      <div className="text-center p-6 border border-dashed rounded-lg">
                        <p className="text-muted-foreground">No team members yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            
              {/* Role information */}
              <Card>
                <CardContent className="pt-6">
                  <div className="bg-muted/40 rounded-lg p-3 text-xs text-muted-foreground">
                    <p><strong>Roles Explained:</strong></p>
                    <ul className="mt-1 space-y-1">
                      <li className="flex items-center gap-1.5">
                        <Check className="h-3.5 w-3.5 text-blue-500" /> <strong>Viewer:</strong> Can view projects and check-ins but cannot edit
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="h-3.5 w-3.5 text-blue-500" /> <strong>Editor:</strong> Can create and edit projects and check-ins, and invite viewers
                      </li>
                      <li className="flex items-center gap-1.5">
                        <Check className="h-3.5 w-3.5 text-blue-500" /> <strong>Admin:</strong> Full access including team management and billing settings
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card className="mt-6 border-destructive/20">
                <CardHeader>
                  <CardTitle className="text-destructive">Danger Zone</CardTitle>
                  <CardDescription>
                    Irreversible and destructive actions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-destructive">Delete Account</h3>
                    <p className="text-sm text-muted-foreground">
                      Permanently delete your account and all associated data.
                      {userRole === 'admin' && ' If you are the only admin, you must assign a new admin first.'}
                    </p>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => navigate('/delete-account')}
                    >
                      Delete Account
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="integrations" className="mt-4 sm:mt-6">
          <Integrations />
        </TabsContent>
        
        <TabsContent value="billing" className="mt-4 sm:mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Billing Information</CardTitle>
              <CardDescription>
                Manage your subscription and payment methods
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg mb-4 gap-3 sm:gap-0">
                <div>
                  <h3 className="font-medium">Current Plan</h3>
                  <p className="text-muted-foreground text-sm">Free</p>
                </div>
                <Button className="w-full sm:w-auto">Upgrade to Pro</Button>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Upgrade to Pro for advanced features, unlimited projects, and priority support.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}